/**
 * Sprint 22 — CostFooter.
 *
 * Footer of the chat panel. Totals tokens + tool calls for the current session
 * by scanning the stream's `completion` data parts (same source as the per-message
 * ChatCompletionLine). Per-session $ is deferred to v1.1 — the worker tracks
 * cost_usd per conversation in ai_usage, surfaced in the Sprint 8.0e cost report;
 * we avoid duplicating pricing in the client.
 *
 * Sprint 24.6 — also surfaced the prompt-cache hit rate. Sprint 24.13 (S24.13.10)
 * replaces the plain session "% cached" string with the colour-banded, per-turn
 * <CacheHitIndicator> (the latest turn is the actionable signal — a scope change
 * or new conversation resets the cache). The worker emits cacheReadTokens /
 * cacheWriteTokens / inputTokens on each `completion` part.
 */

import { type CSSProperties, useMemo } from "react";
import type { UIMessage } from "@ai-sdk/react";
import { CacheHitIndicator } from "./CacheHitIndicator";

interface CostFooterProps {
  messages: UIMessage[];
}

interface SessionTotals {
  tokens: number;
  toolCalls: number;
  // Sprint 24.7 — schema-loaded vs deferred tool counts. Sprint 24.13 — latest
  // turn's cache tokens. All four describe the LATEST turn (not a running total),
  // so we keep the most recent value rather than summing.
  toolsLoaded: number;
  toolsDeferred: number;
  latestCachedTokens: number;
  latestTotalInput: number;
  /** True once at least one completion part carried cache fields (Sprint 24.13). */
  sawCacheData: boolean;
}

function aggregate(messages: UIMessage[]): SessionTotals {
  let tokens = 0;
  let toolCalls = 0;
  let toolsLoaded = 0;
  let toolsDeferred = 0;
  let latestCachedTokens = 0;
  let latestTotalInput = 0;
  let sawCacheData = false;
  for (const msg of messages) {
    if (msg.role !== "assistant" || !msg.parts) continue;
    for (const rawPart of msg.parts) {
      const part = rawPart as unknown as Record<string, unknown>;
      if (part?.type !== "data" || !Array.isArray(part.data)) continue;
      for (const item of part.data) {
        if (typeof item === "object" && item !== null && (item as Record<string, unknown>).type === "completion") {
          const c = item as Record<string, unknown>;
          const input = (c.inputTokens as number) ?? 0;
          const cacheRead = (c.cacheReadTokens as number) ?? 0;
          const cacheWrite = (c.cacheWriteTokens as number) ?? 0;
          tokens += input + ((c.outputTokens as number) ?? 0);
          toolCalls += (c.toolCalls as number) ?? 0;
          // Latest turn wins (overwrite, don't sum).
          if (typeof c.toolsLoaded === "number") toolsLoaded = c.toolsLoaded;
          if (typeof c.toolsDeferred === "number") toolsDeferred = c.toolsDeferred;
          if (typeof c.cacheReadTokens === "number" || typeof c.cacheWriteTokens === "number") {
            sawCacheData = true;
            latestCachedTokens = cacheRead;
            latestTotalInput = input + cacheRead + cacheWrite;
          }
        }
      }
    }
  }
  return { tokens, toolCalls, toolsLoaded, toolsDeferred, latestCachedTokens, latestTotalInput, sawCacheData };
}

export function CostFooter({ messages }: CostFooterProps) {
  const { tokens, toolCalls, toolsLoaded, toolsDeferred, latestCachedTokens, latestTotalInput, sawCacheData } = useMemo(
    () => aggregate(messages),
    [messages],
  );
  if (tokens === 0 && toolCalls === 0) return null;

  const parts = [
    `${tokens.toLocaleString()} tokens`,
    toolCalls > 0 ? `${toolCalls} tool call${toolCalls === 1 ? "" : "s"}` : null,
    // Sprint 24.7 — only worth surfacing when something was actually deferred.
    toolsDeferred > 0 ? `${toolsLoaded} tools · ${toolsDeferred} deferred` : null,
  ].filter(Boolean);

  return (
    <div style={footer}>
      <span style={{ opacity: 0.7 }}>This session</span>
      <span>·</span>
      <span style={{ fontVariantNumeric: "tabular-nums" }}>{parts.join(" · ")}</span>
      {sawCacheData && (
        <>
          <span>·</span>
          <CacheHitIndicator cachedTokens={latestCachedTokens} totalInputTokens={latestTotalInput} />
        </>
      )}
    </div>
  );
}

const footer: CSSProperties = {
  flex: "0 0 auto",
  display: "flex",
  alignItems: "center",
  gap: 6,
  padding: "6px 14px",
  borderTop: "var(--kuvio-border-default)",
  fontSize: 11,
  color: "var(--color-text-tertiary)",
  fontFamily: "var(--font-sans)",
};
